import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Trophy, Ticket, Volume2, VolumeX, X } from "lucide-react";

/**
 * Full-screen "suspense" overlay for prize draws.
 *  phase = "countdown" → 5…1 with a rolling ticket number and ticks
 *  phase = "winners"   → confetti + staggered winner cards
 * Rendered through a portal (works from inside a Radix Dialog).
 */
const GOLD = "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)";
const CONFETTI_COLORS = ["#d4af37", "#f5e4bf", "#ffffff", "#f472b6", "#60a5fa", "#34d399", "#fbbf24"];

export default function DrawReveal({
  open,
  phase,
  seconds = 5,
  participants = 0,
  winners = [],
  showTickets = false,
  onClose,
}) {
  const { t } = useTranslation();
  const [count, setCount] = useState(seconds);
  const [ticketNo, setTicketNo] = useState(1);
  const [soundOn, setSoundOn] = useState(true);
  const ctxRef = useRef(null);
  const soundOnRef = useRef(true);
  soundOnRef.current = soundOn;

  const audio = () => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new Ctx();
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume().catch(() => {});
    return ctxRef.current;
  };
  const tick = (pitch = 880) => {
    if (!soundOnRef.current) return;
    try {
      const ctx = audio();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = pitch;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
      o.connect(g).connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.14);
    } catch (e) { /* ignore */ }
  };
  const fanfare = () => {
    if (!soundOnRef.current) return;
    try {
      const ctx = audio();
      [[523.25, 0], [659.25, 0.08], [783.99, 0.16], [1046.5, 0.26], [1318.5, 0.26]].forEach(([f, d]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        const t0 = ctx.currentTime + d;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.4);
        o.connect(g).connect(ctx.destination);
        o.start(t0);
        o.stop(t0 + 1.5);
      });
    } catch (e) { /* ignore */ }
  };

  // Countdown + rolling ticket number
  useEffect(() => {
    if (!open || phase !== "countdown") return;
    setCount(seconds);
    tick(660);
    const step = setInterval(() => {
      setCount((c) => {
        const n = Math.max(0, c - 1);
        if (n > 0) tick(660 + (seconds - n) * 60);
        return n;
      });
    }, 1000);
    const roll = setInterval(() => {
      setTicketNo(1 + Math.floor(Math.random() * Math.max(1, participants)));
    }, 70);
    return () => {
      clearInterval(step);
      clearInterval(roll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, seconds, participants]);

  useEffect(() => {
    if (open && phase === "winners") fanfare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && phase === "winners") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, phase, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="draw-reveal"
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8"
        style={{ pointerEvents: "auto" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="draw-reveal-overlay"
        data-phase={phase}
      >
        {/* backdrop */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(80% 60% at 50% 40%, rgba(212,175,55,0.22) 0%, rgba(8,6,12,0.94) 60%, rgba(4,3,8,0.98) 100%)",
            backdropFilter: "blur(10px)",
          }}
        />
        <Spotlights />

        <button
          type="button"
          onClick={() => setSoundOn((s) => !s)}
          className="absolute right-4 top-4 z-10 rounded-full border border-white/15 bg-white/5 p-2.5 text-white/70 transition hover:bg-white/10 hover:text-white"
          data-testid="draw-reveal-sound-toggle"
          aria-label="toggle sound"
        >
          {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>

        <div className="relative z-10 w-full max-w-2xl text-center">
          {phase === "countdown" ? (
            <div data-testid="draw-reveal-countdown">
              <div className="text-[11px] font-bold uppercase tracking-[0.42em] text-amber-200/80">
                {t("draw_reveal.countdown_eyebrow")}
              </div>
              <div className="mt-2 text-2xl font-extrabold text-white sm:text-3xl">
                {t("draw_reveal.countdown_title")}
              </div>

              <div className="relative mx-auto mt-8 h-52 w-52 sm:h-64 sm:w-64">
                <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full -rotate-90">
                  <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                  <motion.circle
                    cx="50"
                    cy="50"
                    r="46"
                    fill="none"
                    stroke="#d4af37"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="289"
                    initial={{ strokeDashoffset: 0 }}
                    animate={{ strokeDashoffset: 289 }}
                    transition={{ duration: seconds, ease: "linear" }}
                  />
                </svg>
                <div
                  aria-hidden
                  className="absolute inset-4 rounded-full"
                  style={{ background: "radial-gradient(circle, rgba(212,175,55,0.3), transparent 70%)", filter: "blur(10px)" }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.div
                      key={count}
                      initial={{ scale: 1.8, opacity: 0, filter: "blur(6px)" }}
                      animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
                      exit={{ scale: 0.6, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="font-black tabular-nums text-white"
                      style={{
                        fontSize: count > 0 ? "clamp(88px, 18vw, 140px)" : "28px",
                        lineHeight: 1,
                        textShadow: "0 0 40px rgba(212,175,55,0.55)",
                      }}
                      data-testid="draw-reveal-count"
                    >
                      {count > 0 ? count : t("draw_reveal.drawing")}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              <div className="mx-auto mt-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm text-white/80">
                <Ticket className="h-4 w-4 text-amber-300" />
                <span className="font-mono text-base font-bold tabular-nums text-white" data-testid="draw-reveal-ticket">
                  #{String(ticketNo).padStart(String(Math.max(1, participants)).length, "0")}
                </span>
                <span className="text-white/40">·</span>
                <span>{t("draw_reveal.in_the_urn", { count: participants })}</span>
              </div>
            </div>
          ) : (
            <div data-testid="draw-reveal-winners">
              <ConfettiRain />
              <motion.div
                initial={{ scale: 0.4, opacity: 0, rotate: -12 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 16 }}
                className="mx-auto flex h-20 w-20 items-center justify-center rounded-full"
                style={{ background: GOLD, boxShadow: "0 24px 60px -16px rgba(212,175,55,0.8)" }}
              >
                <Trophy className="h-9 w-9 text-[#1a1410]" />
              </motion.div>
              <motion.div
                initial={{ y: 16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="mt-5 text-[11px] font-bold uppercase tracking-[0.42em] text-amber-200/80"
              >
                {t("draw_reveal.winners_eyebrow")}
              </motion.div>
              <motion.h2
                initial={{ y: 16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="mt-2 text-3xl font-black text-white sm:text-4xl"
              >
                {winners.length > 1 ? t("draw_reveal.winners_title_plural") : t("draw_reveal.winners_title")}
              </motion.h2>

              <ul className="mx-auto mt-7 max-h-[46vh] w-full max-w-xl space-y-3 overflow-y-auto pr-1 text-left">
                {winners.map((w, i) => (
                  <motion.li
                    key={w.session_id || i}
                    initial={{ opacity: 0, y: 28, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: 0.55 + i * 0.35, type: "spring", stiffness: 240, damping: 20 }}
                    className="flex items-center gap-4 rounded-2xl border border-amber-300/25 bg-white/[0.06] px-4 py-3 backdrop-blur-md"
                    data-testid={`draw-reveal-winner-${i}`}
                  >
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-black"
                      style={{ background: GOLD, color: "#1a1410" }}
                    >
                      {i + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-lg font-bold text-white">
                        {w.first_name} {w.last_name}
                      </div>
                      <div className="truncate font-mono text-xs text-white/55">
                        {w.email}
                        {w.phone ? ` · ${w.phone}` : ""}
                      </div>
                    </div>
                    {showTickets && w.tickets != null && (
                      <div className="shrink-0 rounded-full bg-amber-400/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-200">
                        {w.tickets} {t("lottery.tickets")}
                      </div>
                    )}
                  </motion.li>
                ))}
              </ul>

              <motion.button
                type="button"
                onClick={onClose}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 + winners.length * 0.35 }}
                className="mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.2em]"
                style={{ background: GOLD, color: "#1a1410", boxShadow: "0 14px 34px -12px rgba(212,175,55,0.7)" }}
                data-testid="draw-reveal-close-btn"
              >
                <X className="h-3.5 w-3.5" />
                {t("draw_reveal.close")}
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function Spotlights() {
  return (
    <>
      {[-1, 1].map((side) => (
        <motion.div
          key={side}
          aria-hidden
          className="pointer-events-none absolute top-[-20%] h-[140%] w-[28%] origin-top"
          style={{
            left: side < 0 ? "8%" : "auto",
            right: side > 0 ? "8%" : "auto",
            background: "linear-gradient(180deg, rgba(245,228,191,0.18) 0%, rgba(245,228,191,0) 80%)",
            clipPath: "polygon(45% 0, 55% 0, 100% 100%, 0 100%)",
            filter: "blur(6px)",
          }}
          animate={{ rotate: side < 0 ? [-14, 12, -14] : [14, -12, 14] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </>
  );
}

function ConfettiRain() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 110 }).map((_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 1.6,
        duration: 2.6 + Math.random() * 2.2,
        w: 6 + Math.random() * 6,
        h: 10 + Math.random() * 10,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        sway: -60 + Math.random() * 120,
        spin: 360 + Math.random() * 720,
        round: Math.random() > 0.7,
      })),
    []
  );
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          className={`absolute top-[-6%] ${p.round ? "rounded-full" : "rounded-[2px]"}`}
          style={{ left: `${p.x}%`, width: p.w, height: p.round ? p.w : p.h, background: p.color }}
          initial={{ y: 0, x: 0, rotate: 0, opacity: 0 }}
          animate={{ y: "115vh", x: p.sway, rotate: p.spin, opacity: [0, 1, 1, 0.9] }}
          transition={{ duration: p.duration, delay: p.delay, ease: [0.3, 0.6, 0.7, 1], repeat: 1, repeatDelay: 0.6 }}
        />
      ))}
    </div>
  );
}
